import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AppAdminEntry } from '@/admin/useAdminApps';
import { useSoftDeleteApp, useUpdateApp } from '@/admin/useAdminApps';
import { AppIconEditor } from '@/components/admin/AppIconEditor';
import { PermissionsPanel } from '@/components/admin/PermissionsPanel';

export interface AppsTableProps {
  apps: AppAdminEntry[];
}

export function AppsTable({ apps }: AppsTableProps) {
  const { t } = useTranslation();
  const del = useSoftDeleteApp();
  const update = useUpdateApp();
  const [expanded, setExpanded] = useState<number | null>(null);

  if (apps.length === 0) {
    return (
      <p className="rounded-md border border-dashed bg-white p-6 text-center text-sm text-slate-500">
        {t('admin.table.empty')}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border shadow-sm">
      <table className="w-full min-w-[56rem] bg-white text-sm">
      <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
        <tr>
          <th className="px-3 py-2">{t('admin.icon.column')}</th>
          <th className="px-3 py-2">{t('admin.fields.slug')}</th>
          <th className="px-3 py-2">{t('admin.fields.name')}</th>
          <th className="px-3 py-2">{t('admin.fields.url')}</th>
          <th className="px-3 py-2">{t('admin.fields.enabled')}</th>
          <th className="px-3 py-2">{t('admin.fields.public')}</th>
          <th className="px-3 py-2 text-right">{t('admin.table.actions')}</th>
        </tr>
      </thead>
      <tbody>
        {apps.map((app) => {
          const isSoftDeleted = app.soft_deleted_at !== null;
          return (
            <Fragment key={app.id}>
              <tr
                className={
                  isSoftDeleted ? 'text-slate-400 line-through' : 'text-slate-900'
                }
              >
                <td className="px-3 py-2 no-underline">
                  <AppIconEditor app={app} />
                </td>
                <td className="px-3 py-2 font-mono text-xs">{app.slug}</td>
                <td className="px-3 py-2">{app.name}</td>
                <td className="truncate px-3 py-2 text-slate-500">{app.url}</td>
                <td className="px-3 py-2">
                  {app.enabled ? t('admin.table.yes') : t('admin.table.no')}
                </td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={app.is_public}
                    aria-label={t('admin.table.togglePublic', { name: app.name })}
                    disabled={isSoftDeleted || update.isPending}
                    onClick={() =>
                      update.mutate({ id: app.id, body: { is_public: !app.is_public } })
                    }
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition disabled:opacity-50 ${
                      app.is_public
                        ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {app.is_public ? t('admin.table.yes') : t('admin.table.no')}
                  </button>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() =>
                      setExpanded((prev) => (prev === app.id ? null : app.id))
                    }
                    className="mr-2 text-xs text-appsol-900 hover:underline"
                  >
                    {expanded === app.id
                      ? t('admin.table.hidePermissions')
                      : t('admin.table.showPermissions')}
                  </button>
                  {!isSoftDeleted && (
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(t('admin.table.confirmDelete', { name: app.name }))) {
                          del.mutate(app.id);
                        }
                      }}
                      disabled={del.isPending}
                      className="text-xs text-rose-700 hover:underline disabled:opacity-50"
                    >
                      {t('admin.table.delete')}
                    </button>
                  )}
                </td>
              </tr>
              {expanded === app.id && (
                <tr>
                  <td colSpan={7} className="bg-slate-50 px-3 py-3">
                    <PermissionsPanel appId={app.id} />
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
      </table>
    </div>
  );
}
